import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useProgramSessionDates } from "../../../hooks/useProgramSessionDates";

interface EditEnrollmentDialogProps {
  open: boolean;
  enrollment: any;
  onClose: () => void;
  onUpdate: (enrollmentId: string, data: any) => Promise<void>;
}

export const EditEnrollmentDialog = ({ open, enrollment, onClose, onUpdate }: EditEnrollmentDialogProps) => {
  const [editForm, setEditForm] = useState({
    selectedStartDate: "",
    timeSlot: "",
    participantAge: "",
    paymentStatus: "",
  });
  const [saving, setSaving] = useState(false);

  const { dateOptions, timeSlots } = useProgramSessionDates(
    enrollment?.session || null,
    editForm.selectedStartDate
  );

  // Initialize form when enrollment changes
  useEffect(() => {
    if (enrollment) {
      setEditForm({
        selectedStartDate: enrollment.selectedStartDate || "",
        timeSlot: enrollment.timeSlot || "",
        participantAge: enrollment.participantAge || "",
        paymentStatus: enrollment.paymentStatus || "pending",
      });
    }
  }, [enrollment]);

  const handleUpdate = async () => {
    if (!enrollment || !editForm.selectedStartDate) return;
    setSaving(true);
    try {
      await onUpdate(enrollment.id, {
        selectedStartDate: editForm.selectedStartDate,
        timeSlot: editForm.timeSlot,
        participantAge: editForm.participantAge,
        paymentStatus: editForm.paymentStatus,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  if (!enrollment) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm" 
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={700}>
            Edit Enrollment
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 3 }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Participant: {enrollment.participantName}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Session: {enrollment.session?.name}
            </Typography>
          </Box>

          <TextField
            select
            fullWidth
            label="Select New Start Date"
            value={editForm.selectedStartDate}
            onChange={(e) => setEditForm({ ...editForm, selectedStartDate: e.target.value, timeSlot: "" })}
            helperText="Changing the start date will update the end date automatically"
          >
            {dateOptions.map((date) => (
              <MenuItem key={date} value={date}>
                {new Date(date + "T00:00:00").toLocaleDateString([], {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            fullWidth
            label="Select Time Slot"
            value={editForm.timeSlot}
            onChange={(e) => setEditForm({ ...editForm, timeSlot: e.target.value })}
            disabled={!editForm.selectedStartDate || !timeSlots.length}
            helperText={!editForm.selectedStartDate ? "Please select a date first" : ""}
          >
            {timeSlots.map((slot) => (
              <MenuItem key={slot} value={slot}>
                {slot.replace("-", " - ")}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            fullWidth
            label="Age Group"
            value={editForm.participantAge}
            onChange={(e) => setEditForm({ ...editForm, participantAge: e.target.value })}
            helperText="Update the participant's age group"
          >
            {enrollment?.session?.ageGroups?.map((age: string) => (
              <MenuItem key={age} value={age}>
                {age}
              </MenuItem>
            )) || <MenuItem disabled>No age groups available</MenuItem>}
          </TextField>

          <TextField
            select
            fullWidth
            label="Payment Status"
            value={editForm.paymentStatus}
            onChange={(e) => setEditForm({ ...editForm, paymentStatus: e.target.value })}
            helperText="Change the payment/enrollment status"
          >
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="success">Success/Confirmed</MenuItem>
            <MenuItem value="paid">Paid</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button onClick={onClose} sx={{ borderRadius: 2 }} disabled={saving}>
          Cancel
        </Button>
        <Button 
          onClick={handleUpdate} 
          variant="contained"
          disabled={!editForm.selectedStartDate || saving}
          sx={{ borderRadius: 2 }}
        >
          {saving ? "Updating..." : "Update Enrollment"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

